import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Twitter, Linkedin, Instagram, Mail } from "lucide-react";
import Hls from "hls.js";

const VIDEO_URL = "https://stream.mux.com/Kec29dVyJgiPdtWaQtPuEiiGHkJIYQAVUJcNiIHUYeo.m3u8";

export default function Footer() {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(VIDEO_URL);
      hls.attachMedia(video);
      return () => hls.destroy();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = VIDEO_URL;
    }
  }, []);

  return (
    <footer className="relative bg-black border-t border-white/10 pt-16 pb-8 px-4 sm:px-8 lg:px-16 overflow-hidden" data-testid="global-footer">
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover object-[center_65%] pointer-events-none opacity-50"
      />

      {/* Top gradient */}
      <div
        className="absolute top-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: "150px",
          background: "linear-gradient(to top, transparent, black)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-12 mb-16">
        
        {/* Brand Section */}
        <div className="max-w-sm">
          <Link to="/" className="text-2xl font-heading italic text-white tracking-tight inline-block mb-4">
            EsyAI.
          </Link>
          <p className="text-white/60 font-body text-sm leading-relaxed mb-6">
            We design and build premium websites, custom CRM tools, and intelligent automations to help your business scale effortlessly.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-white/50 hover:text-white transition-colors">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="#" className="text-white/50 hover:text-white transition-colors">
              <Linkedin className="w-5 h-5" />
            </a>
            <a href="#" className="text-white/50 hover:text-white transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="mailto:hello@esyai.com" className="text-white/50 hover:text-white transition-colors">
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Links Section */}
        <div className="flex gap-16 md:gap-24 flex-wrap">
          <div>
            <h4 className="text-white font-medium font-body mb-6 text-sm">Navigation</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/" className="text-white/60 hover:text-white text-sm font-body transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/projects" className="text-white/60 hover:text-white text-sm font-body transition-colors">Projects</Link>
              </li>
              <li>
                <Link to="/services" className="text-white/60 hover:text-white text-sm font-body transition-colors">Services</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-medium font-body mb-6 text-sm">Company</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/contact" className="text-white/60 hover:text-white text-sm font-body transition-colors">Contact Us</Link>
              </li>
              <li>
                <Link to="/privacy" className="text-white/60 hover:text-white text-sm font-body transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/terms" className="text-white/60 hover:text-white text-sm font-body transition-colors">Terms of Service</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 max-w-7xl mx-auto border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-white/40 text-xs font-body">
          &copy; {new Date().getFullYear()} EsyAI. All rights reserved.
        </p>
        <div className="flex items-center gap-6">
          <span className="text-white/40 text-xs font-body">
            Designed & Built for Performance
          </span>
        </div>
      </div>
    </footer>
  );
}
